import React, { useEffect } from "react";

import { Drawer as AntDrawer, DrawerProps as AntDrawerProps } from "antd";

import { ProductEventProps, productAnalytics, trackMixpanel, useAppType } from "utils";

import { Icon, Link, Svg, Typography } from "components";

export type DrawerProps = AntDrawerProps &
  ProductEventProps & {
    visible: boolean;
    dataTestId?: string;
    fullHeight?: boolean;
    fullWidth?: boolean;
    isNavDrawer?: boolean;
    logoUrl?: string;
    wide?: boolean;
    zIndex?: number;
    hideCloseIcon?: boolean;
    titleVariant?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
  };

export const Drawer = ({
  visible,
  children,
  className = "",
  dataTestId = "",
  destroyOnHidden = true,
  footer,
  fullHeight = false,
  fullWidth = false,
  isNavDrawer = false,
  logoUrl = "",
  onClick = () => {},
  onClose = () => {},
  placement,
  title,
  wide = false,
  zIndex = 1002,
  hideCloseIcon,
  titleVariant = "h4",
  productEvent,
  productEventProperties,
  ...props
}: DrawerProps) => {
  const appType = useAppType();

  const getWidth = () => {
    if (props.width) {
      return props.width;
    }
    if (!appType.desktop && isNavDrawer) {
      return "20rem";
    }
    if (fullWidth || !appType.desktop) {
      return "100vw";
    }
    if (wide) {
      return "75vw";
    }
    return "644px";
  };

  const getPlacement = () => {
    if (placement) {
      return placement;
    }
    if (!appType.desktop && isNavDrawer) {
      return "left";
    }
    if (!appType.desktop) {
      return "bottom";
    }
    return "right";
  };

  useEffect(() => {
    if ((title || className) && visible) {
      trackMixpanel("DrawerOpened", { type: title || className });
    }
  }, [visible, className, title]);

  useEffect(() => {
    if (visible && productEvent) productAnalytics.track(productEvent, productEventProperties);
    // We omit the properties from the deps. An inline object would send the open event again on each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, productEvent]);

  return (
    <AntDrawer
      data-testid={dataTestId}
      rootClassName={`klim-drawer ${footer ? "has-footer" : ""} ${fullHeight ? "fullHeight" : ""} ${className}`}
      footer={footer}
      height={fullHeight ? "100vh" : props.height}
      placement={getPlacement()}
      title={
        <>
          {isNavDrawer && appType.mobile && (
            <Link to={logoUrl ?? "/"} onClick={onClose} mixpanelText={title as string}>
              <Svg svgName="logos/klim_logo_text_black" width={75} height={32} />
            </Link>
          )}
          <Typography variant={titleVariant}>{title}</Typography>
        </>
      }
      open={visible}
      width={getWidth()}
      onClick={onClick}
      onClose={onClose}
      closeIcon={hideCloseIcon ? null : <Icon iconName="tabler/x" size="m" dataTestId={"drawer-close-button"} />}
      destroyOnHidden={destroyOnHidden}
      zIndex={zIndex}
      {...props}
    >
      {children}
    </AntDrawer>
  );
};
